/* =============================================================================
   HHM MOTION SYSTEM — JavaScript Engine
   Lightweight, dependency-free. Drives scroll reveals, hero entrance,
   nav shadow, smooth accordions, and lazy-image fades.
   Premium motion at ~3kb. No external libraries.
   ============================================================================= */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     PREFERS-REDUCED-MOTION
     All animation opt-in; reduced-motion skips spatial transitions entirely.
     -------------------------------------------------------------------------- */
  var reducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  /* --------------------------------------------------------------------------
     HERO ENTRANCE
     Adds .js-motion-ready to <html> after the first paint so CSS transitions
     fire once and never repeat. Uses double-rAF to ensure layout is complete.
     -------------------------------------------------------------------------- */
  function initHeroEntrance() {
    // CSS handles the actual animation; we just need to flip the class.
    // Double rAF: first frame commits styles, second begins transition.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        document.documentElement.classList.add('js-motion-ready');
      });
    });
  }

  /* --------------------------------------------------------------------------
     NAVIGATION SCROLL SHADOW
     Adds .is-scrolled to #HHMHeader after 20px of scroll.
     Throttled with rAF so it never blocks the main thread.
     -------------------------------------------------------------------------- */
  function initNavScroll() {
    var header = document.getElementById('HHMHeader');
    if (!header) return;

    var ticking = false;

    function update() {
      header.classList.toggle('is-scrolled', window.scrollY > 20);
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  }

  /* --------------------------------------------------------------------------
     SCROLL REVEALS
     Two-tier system:
       1. Section-level: .hhm-section-reveal — whole section fades up 10px
       2. Card grids: child items stagger 60ms apart
     Elements already in the viewport at init get .is-revealed immediately
     (no transition class), so above-fold content is never animated.
     -------------------------------------------------------------------------- */
  function initScrollReveals() {
    if (reducedMotion) return;

    var vph = window.innerHeight;

    /* ---- Section-level reveals ---- */
    var sectionTargets = document.querySelectorAll(
      '.shopify-section > section, ' +
      '.shopify-section > div[style*="background"], ' +
      '.shopify-section > div[style*="padding"]'
    );

    var sectionsToObserve = [];

    sectionTargets.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < vph * 0.85) {
        // Already visible — show immediately, no transition
        return;
      }
      el.classList.add('hhm-section-reveal');
      sectionsToObserve.push(el);
    });

    if (sectionsToObserve.length > 0) {
      var sectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            sectionObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.04,
        rootMargin: '0px 0px -32px 0px'
      });

      sectionsToObserve.forEach(function (el) {
        sectionObserver.observe(el);
      });
    }

    /* ---- Card grid stagger reveals ---- */
    // Finds card wrappers inside product grids and stagger-reveals them.
    var gridSelectors = [
      /* PDP + inner page grids */
      '.box-journey-grid > div',
      '.bpe-duo > div',
      '.pdp-persona-card',
      '.parasite-perks-grid > div',
      '[style*="grid-template-columns: repeat"] > div',
      '[style*="grid-template-columns:repeat"] > div',
      /* Homepage section stagger targets */
      '.hp-trust-cell',
      '.hp-benefit-card',
      '.hp-bs-card',
      '.hp-box-grid > div',
      '.hp-proof-grid > div',
      '.hp-bento-card',
      'details.hhm-faq-item',
      /* PDP testimonials grid */
      '.hhm-testimonial-card'
    ];

    var cardObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          cardObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.06,
      rootMargin: '0px 0px -24px 0px'
    });

    gridSelectors.forEach(function (sel) {
      var cards;
      try { cards = document.querySelectorAll(sel); } catch (e) { return; }

      var groupMap = new Map();
      cards.forEach(function (card) {
        var parent = card.parentElement;
        if (!groupMap.has(parent)) groupMap.set(parent, []);
        groupMap.get(parent).push(card);
      });

      groupMap.forEach(function (group) {
        group.forEach(function (card, i) {
          var rect = card.getBoundingClientRect();
          if (rect.top < vph * 0.85) return; // Skip above-fold cards

          card.classList.add('hhm-will-reveal');
          if (i > 0) card.setAttribute('data-delay', String(Math.min(i, 5)));
          cardObserver.observe(card);
        });
      });
    });
  }

  /* --------------------------------------------------------------------------
     SMOOTH ACCORDIONS
     Upgrades all details.hhm-faq-item with buttery max-height transitions.
     Intercepts the native toggle to add animation; sets/removes open manually.
     -------------------------------------------------------------------------- */
  function initAccordions() {
    document.querySelectorAll('details.hhm-faq-item').forEach(function (details) {
      var summary = details.querySelector('summary, .hhm-faq-item__btn');
      var content = details.querySelector('.hhm-faq-item__answer');
      if (!summary || !content) return;

      var isAnimating = false;
      var ease = 'cubic-bezier(.22,.61,.36,1)';

      // Prepare content for animation
      content.style.overflow = 'hidden';
      if (!details.open) {
        content.style.maxHeight = '0px';
        content.style.opacity = '0';
      }

      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (isAnimating) return;
        isAnimating = true;

        if (details.open) {
          /* ---- Closing ---- */
          var startH = content.scrollHeight;
          content.style.maxHeight = startH + 'px';
          content.style.transition =
            'max-height 260ms ' + ease + ', opacity 200ms ease-out';

          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              content.style.maxHeight = '0px';
              content.style.opacity = '0';
            });
          });

          function onCloseDone(ev) {
            if (ev.propertyName !== 'max-height') return;
            content.removeEventListener('transitionend', onCloseDone);
            details.removeAttribute('open');
            isAnimating = false;
          }
          content.addEventListener('transitionend', onCloseDone);

        } else {
          /* ---- Opening ---- */
          details.setAttribute('open', '');
          var targetH = content.scrollHeight;
          content.style.maxHeight = '0px';
          content.style.opacity = '0';
          content.style.transition =
            'max-height 340ms ' + ease + ', opacity 280ms ease-out';

          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              content.style.maxHeight = targetH + 'px';
              content.style.opacity = '1';
            });
          });

          function onOpenDone(ev) {
            if (ev.propertyName !== 'max-height') return;
            content.removeEventListener('transitionend', onOpenDone);
            content.style.maxHeight = 'none'; // Allow natural reflow
            isAnimating = false;
          }
          content.addEventListener('transitionend', onOpenDone);
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     LAZY IMAGE FADE
     Adds .hhm-img-fade to all unloaded lazy images. On the load event,
     adds .is-loaded which CSS transitions to opacity:1.
     -------------------------------------------------------------------------- */
  function initImageFade() {
    if (reducedMotion) return;

    document.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) return; // Already loaded
      img.classList.add('hhm-img-fade');
      img.addEventListener('load', function () {
        img.classList.add('is-loaded');
      }, { once: true });
      // Fallback: if load never fires, reveal after 2s
      setTimeout(function () {
        img.classList.add('is-loaded');
      }, 2000);
    });
  }

  /* --------------------------------------------------------------------------
     DATA-MOTION ELEMENT REVEALS
     Observes all [data-motion] elements and adds .is-revealed on entry.
     Elements already above fold get .is-revealed immediately (no transition).
     -------------------------------------------------------------------------- */
  function initDataMotion() {
    var elements = document.querySelectorAll('[data-motion]');
    if (!elements.length) return;

    var vph = window.innerHeight;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.06,
      rootMargin: '0px 0px -24px 0px'
    });

    elements.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < vph * 0.9) {
        // Already visible — reveal instantly without transition
        el.classList.add('is-revealed');
        return;
      }
      observer.observe(el);
    });
  }

  /* --------------------------------------------------------------------------
     BUTTON HOVER AUTO-TAGGER
     Finds inline-style <a> and <button> elements that look like buttons
     (have border-radius + background) and adds .hhm-auto-btn so CSS hover
     states apply without touching every section file.
     -------------------------------------------------------------------------- */
  function initButtonHovers() {
    document.querySelectorAll('a[style], button[style]').forEach(function (el) {
      var cls = el.getAttribute('class') || '';
      // Skip elements that already have explicit hhm-btn handling
      if (cls.indexOf('hhm-btn') !== -1 || cls.indexOf('hhm-auto-btn') !== -1) return;

      var s = el.getAttribute('style') || '';
      if (s.indexOf('border-radius') !== -1 && s.indexOf('background') !== -1) {
        el.classList.add('hhm-auto-btn');
      } else if (s.indexOf('border-bottom') !== -1 &&
                 s.indexOf('background:') === -1 &&
                 s.indexOf('background-color') === -1) {
        el.classList.add('hhm-auto-link');
      }
    });
  }

  /* --------------------------------------------------------------------------
     CARD HOVER
     Adds .hhm-card-hover to direct children of product grid containers
     so the CSS hover rule picks them up without editing every section file.
     -------------------------------------------------------------------------- */
  function initCardHover() {
    var gridSelectors = [
      '.pdp-persona-card',
      '.box-journey-grid > div',
      '.product-recs-grid > div'
    ];

    gridSelectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.classList.add('hhm-card-hover');
      });
    });

    // featured-collection.liquid grid: inline-style grid children
    document.querySelectorAll('.featured-collection-grid > div').forEach(function (el) {
      el.classList.add('hhm-card-hover');
    });
  }

  /* --------------------------------------------------------------------------
     INIT
     Hero entrance runs synchronously (must happen before DOMContentLoaded
     to avoid flash). Everything else defers to DOMContentLoaded.
     -------------------------------------------------------------------------- */

  // Hero entrance: fire immediately — CSS transitions haven't started yet.
  initHeroEntrance();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  function onReady() {
    initNavScroll();
    initScrollReveals();
    initDataMotion();
    initAccordions();
    initImageFade();
    initCardHover();
    initButtonHovers();
  }

  // Theme editor: reveal all data-motion elements instantly when a section
  // is added, reloaded, or reordered — avoids permanently-invisible elements.
  if (window.Shopify && window.Shopify.designMode) {
    var revealSection = function(event) {
      var section = event.target;
      section.querySelectorAll('[data-motion]:not(.is-revealed)').forEach(function(el) {
        el.classList.add('is-revealed');
      });
      initButtonHovers();
      initCardHover();
    };
    document.addEventListener('shopify:section:load',    revealSection);
    document.addEventListener('shopify:section:reorder', revealSection);
  }

})();
